import {useEffect, useMemo, useState} from 'react';
import {buFunctionMap, MenuItem} from './types';
import {scrollToTop} from '../../util/utility';
import {functionalitiesAliasMap} from '../../util/strings';
import {get} from '../../api/api';
import {uriGetServices, uriGetServicesBO} from '../../api/endpoints';

export const useVisibleMenuItems = (
  items: MenuItem[],
  busFuncMap: any,
  selectedBu: any,
  user: any
) => {
  const [medicOnline, setMedicoOnline] = useState<any>();
  const [medicOnlineDisabled, setMedicOnlineDisabled] =
    useState<boolean>(false);
  useEffect(() => {
    const getServices = async () => {
      try {
        await get(
          uriGetServices(selectedBu.buId, selectedBu.prdNbr, selectedBu.benNbr),
          false,
          0,
          functionalitiesAliasMap.appointments
        ).then((res: any) => {
          const aux = res?.data?.items?.find((el: any) => el.isMedicoOnline);
          if (aux) {
            setMedicoOnline(aux);
            setMedicOnlineDisabled(false);
          } else {
            setMedicOnlineDisabled(true);
          }
        });
      } catch (err: any) {}
    };
    const getServicesNC = async () => {
      try {
        const finalServices: any[] = [];
        const services: any =
          (await get(
            uriGetServicesBO(),
            false,
            0,
            functionalitiesAliasMap.services
          ).catch(() => {})) || [];
        const activeServices = services?.data?.items?.filter(
          (service: any) =>
            service.status === 'ACTIVE' && service.buAlias === 'NOTCLIENT'
        );
        activeServices.forEach((el: any) => {
          finalServices.push({
            ...el.buServiceDetails.find(
              (detail: any) => detail.languageAlias === user.language
            ),
            serviceId: el.id,
            alias: el.alias,
            displayText: el.buServiceDetails.find(
              (detail: any) => detail.languageAlias === user.language
            ).serviceName,
            image: el.buServiceDetails.find(
              (detail: any) => detail.languageAlias === user.language
            ).image,
            listDescription: el.buServiceDetails.find(
              (detail: any) => detail.languageAlias === user.language
            ).listDescription,
            listDescriptionAPP: el.buServiceDetails.find(
              (detail: any) => detail.languageAlias === user.language
            ).listDescriptionApp,
            serviceName: el.buServiceDetails.find(
              (detail: any) => detail.languageAlias === user.language
            ).serviceName,
            isMedicoOnline: el.isMedicoOnline,
            buttonText: el.buServiceDetails.find(
              (detail: any) => detail.languageAlias === user.language
            ).buttonText,
          });
          const aux = finalServices.find((el: any) => el.isMedicoOnline);
          if (aux) {
            setMedicoOnline(aux);
            setMedicOnlineDisabled(false);
          } else {
            setMedicOnlineDisabled(true);
          }
        });
      } catch (e: any) {}
    };
    if (selectedBu.buId === 'NOTCLIENT') {
      getServicesNC();
    } else if (selectedBu.buId && selectedBu.prdNbr && selectedBu.benNbr) {
      getServices();
    }
  }, [selectedBu.memId]);

  return useMemo(() => {
    if (!busFuncMap || !items || !selectedBu?.buId) return [];

    const result = items.filter((item) => {
      const buFuncs = busFuncMap[selectedBu?.buId];
      if (!buFuncs) return false;

      const hasMatchingFunc = buFuncs.some((func: buFunctionMap) => {
        return func?.isVisible && item.funct === func?.featureTemplateAlias;
      });

      if (!hasMatchingFunc) return false;

      // If item has subItems, at least one must be visible
      if (item.subItems && item.subItems.length > 0) {
        const subItemFuncts = item.subItems.map((s) => s.funct);
        const hasVisibleChild = buFuncs.some(
          (func: buFunctionMap) =>
            func?.isVisible && subItemFuncts.includes(func.featureTemplateAlias)
        );
        return hasVisibleChild;
      }

      return true;
    });
    const cleanedResult = result.map((item: any) => {
      if (!item?.subItems?.length) {
        return item; // não há subitems → nada a remover
      }

      // se a função de consultas online estiver desativada
      if (!medicOnline || medicOnlineDisabled) {
        // remove apenas o subitem 'appointments'
        const filteredSubItems = item.subItems.filter(
          (subItem: MenuItem) =>
            subItem.funct !== functionalitiesAliasMap.appointments
        );

        return {...item, subItems: filteredSubItems};
      }

      return item;
    });

    return cleanedResult;
  }, [items, busFuncMap, selectedBu?.buId, medicOnline, medicOnlineDisabled]);
};

export const useNavSelection = (history: any) => {
  const [selectedItem, setSelectedItem] = useState<string[] | string>([]);
  const [currentPath, setCurrentPath] = useState(history.location.pathname);

  useEffect(() => {
    const unlisten = history.listen((location: any) => {
      setCurrentPath(location.pathname);
    });
    return () => {
      unlisten();
    };
  }, [history]);

  const handleSelect = (item: any) => {
    setSelectedItem(item.selector);
  };

  const handleNavigate = (url: string, selector: string[] | string) => {
    setSelectedItem(selector);
    setCurrentPath(url);
    history.push(url);
    scrollToTop();
  };

  return {
    selectedItem,
    currentPath,
    handleSelect,
    handleNavigate,
  };
};

export const useDropdownState = (initialOpenItem?: string) => {
  const [selectedItem, setSelectedItem] = useState<string | null>(
    initialOpenItem || null
  );

  const toggleItem = (selector: string) => {
    setSelectedItem((prev) => (prev === selector ? null : selector));
  };

  return {
    selectedItem,
    setSelectedItem,
    toggleItem,
    isOpen: (selector: string) => selectedItem === selector,
  };
};
